/**
 * PaymentRequiredModal Component
 *
 * A blocking modal that appears when the backend returns a 402 error.
 * User must subscribe or sign out - cannot dismiss this modal.
 */

import React from "react";
import { Modal, Stack, Title, Text, Button } from "@mantine/core";
import { IconAlertCircle, IconCreditCard } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { signOut } from "aws-amplify/auth";
import styles from "./PaymentRequiredModal.module.css";

interface PaymentRequiredModalProps {
  /** Whether the modal is open */
  opened: boolean;
  /** Error message from the backend */
  message?: string;
  /** Trial end date if trial expired */
  trialEndDt?: string;
  /** Subscription status if subscription issue */
  subscriptionStatus?: string;
}

export const PaymentRequiredModal: React.FC<PaymentRequiredModalProps> = ({
  opened,
  message,
  trialEndDt,
  subscriptionStatus,
}) => {
  const navigate = useNavigate();

  const isTrialExpired = !!trialEndDt;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleSubscribe = () => {
    navigate("/admin/settings/subscription");
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      // Force navigation anyway
      navigate("/login");
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {}} // Cannot close - must take action
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      centered
      size="md"
      classNames={{ content: styles.modalContent }}
    >
      <Stack align="center" gap="lg" py="md">
        <div className={styles.iconWrapper}>
          <IconAlertCircle size={64} className={styles.alertIcon} />
        </div>

        <Title order={2} ta="center">
          {isTrialExpired ? "Trial Expired" : "Subscription Required"}
        </Title>

        <Text ta="center" c="dimmed" size="md">
          {message ||
            (isTrialExpired
              ? "Your free trial has ended. Subscribe to continue using Pagoda."
              : "Your subscription is inactive. Please update your payment method to continue.")}
        </Text>

        {isTrialExpired && trialEndDt && (
          <Text size="sm" c="dimmed">
            Your free trial ended on {formatDate(trialEndDt)}
          </Text>
        )}

        {subscriptionStatus && (
          <Text size="sm" c="red">
            Status: {subscriptionStatus.replace("_", " ")}
          </Text>
        )}

        <Stack gap="sm" w="100%" mt="md">
          <Button
            size="lg"
            color="teal"
            leftSection={<IconCreditCard size={20} />}
            onClick={handleSubscribe}
            fullWidth
          >
            {isTrialExpired ? "Choose a Plan" : "Update Payment"}
          </Button>

          <Button
            variant="subtle"
            color="gray"
            onClick={handleSignOut}
            fullWidth
          >
            Sign Out
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default PaymentRequiredModal;
